import apiClient, { authenticatedFetch } from './apiClient';

export const chatApi = {
  createChat: async (workspaceId = null) => {
    const response = await apiClient.post('/chat/', {
      title: "Untitled Chat",
      workspace_id: workspaceId
    });
    return response.data;
  },

  getMessages: async (chatId) => {
    const response = await apiClient.get(`/chat/${chatId}/messages`);
    return response.data;
  },

  getChats: async (workspaceId = null) => {
    const url = workspaceId ? `/chat/?workspace_id=${workspaceId}` : '/chat/';
    const response = await apiClient.get(url);
    return response.data;
  },

  deleteChat: async (chatId) => {
    const response = await apiClient.delete(`/chat/${chatId}`);
    return response.data;
  },

  generateTitle: async (chatId) => {
    const response = await apiClient.post(`/chat/${chatId}/generate-title`);
    return response.data;
  },

  // Stream chat messages using NDJSON
  streamChat: async (chatId, text, datasets, model = 'openai', onEvent, abortSignal = null) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const response = await authenticatedFetch(`${apiUrl}/chat/${chatId}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, datasets, model }),
      signal: abortSignal
    });

    if (!response.ok) {
      throw new Error(`Chat stream failed: ${response.statusText}`);
    }

    // Ensure we're reading the stream as it arrives
    if (!response.body) {
      throw new Error('Response body is not available for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        // Check if aborted
        if (abortSignal?.aborted) {
          reader.cancel();
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines (NDJSON format: one JSON object per line)
        const lines = buffer.split('\n');
        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        // Process each complete line immediately
        for (const line of lines) {
          // Check if aborted before processing each line
          if (abortSignal?.aborted) {
            reader.cancel();
            break;
          }
          if (line.trim()) {
            try {
              const event = JSON.parse(line);
              // Call the event handler immediately for real-time processing
              onEvent(event);
            } catch (e) {
              console.error('Error parsing NDJSON line:', e, 'Line:', line);
            }
          }
        }
      }

      // Process any remaining data in buffer (only if not aborted)
      if (!abortSignal?.aborted && buffer.trim()) {
        try {
          const event = JSON.parse(buffer.trim());
          onEvent(event);
        } catch (e) {
          console.error('Error parsing final NDJSON line:', e);
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  // Generate map layer directly using NDJSON streaming
  generateMapLayer: async (chatId, text, datasets, model = 'openai', onEvent, abortSignal = null) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const response = await authenticatedFetch(`${apiUrl}/chat/${chatId}/generate-map`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, datasets, model }),
      signal: abortSignal
    });

    if (!response.ok) {
      throw new Error(`Map generation failed: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is not available for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        if (abortSignal?.aborted) {
          reader.cancel();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (abortSignal?.aborted) {
            reader.cancel();
            break;
          }
          if (line.trim()) {
            try {
              const event = JSON.parse(line);
              onEvent(event);
            } catch (e) {
              console.error('Error parsing NDJSON line:', e, 'Line:', line);
            }
          }
        }
      }

      if (!abortSignal?.aborted && buffer.trim()) {
        try {
          const event = JSON.parse(buffer.trim());
          onEvent(event);
        } catch (e) {
          console.error('Error parsing final NDJSON line:', e);
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
};
